// ─────────────────────────────────────────────────────────────
// MARTIAL ARTS KIDS CLASS READY™ — all copy and links live here. Image URLs live in data/images.ts, the pixel in data/pixel.ts.
// Same structure as ABA Session Ready / Team Meeting Ready (modeled on Managers), with a NEW palette (dojo: red buttons,
// gold highlights, charcoal sections), NEW button copy and a NEW section (values + age levels), to test what converts.
// Promise (must match ads, checkout and the PDF): 52-week class plan + 10 complete 45-minute kids classes,
// $27 one-time, 10-day guarantee, the 5 bonuses in the checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// Kids + contact sport: no promises about self-defense, belts or sign-ups; pads and air only, no sparring; no testimonials.
// ─────────────────────────────────────────────────────────────

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://martial-arts-kids-ready.impultienda.ar/checkout';

export const PAGE_TITLE = 'Martial Arts Kids Class Ready™ | Your Whole Year of Kids Classes, Already Planned';
export const META_DESCRIPTION =
  'A 52-week class plan plus 10 complete, ready-to-teach 45-minute kids martial arts classes. Open this week’s page, prepare in 5 minutes and teach it tonight.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'MARTIAL ARTS KIDS CLASS READY™';
/** Label for every button that scrolls to the offer section (new copy for this landing) */
export const ctaLabel = 'PLAN MY WHOLE YEAR';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'PLAN MY WHOLE YEAR · $27';
export const topBarMobileLabel = 'THE BOOK · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR KARATE, TAEKWONDO, KICKBOXING & MUAY THAI INSTRUCTORS',
  titleLead: 'YOUR WHOLE YEAR OF KIDS CLASSES,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'A 52-week class plan plus 10 complete, ready-to-teach 45-minute classes for kids. Open this week’s page and teach it tonight.',
  mockupAlt: 'Martial Arts Kids Class Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'WEEKS PLANNED' },
  { value: '10', label: 'COMPLETE CLASSES' },
  { value: '45', label: 'MINUTES, FULLY TIMED' },
  { value: '3', label: 'AGE LEVELS IN EVERY CLASS' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'THE NIGHT BEFORE VS. ON THE MAT.',
  before: {
    label: 'BEFORE',
    when: 'The night before',
    quote: '“What do I teach the kids tonight?”',
    items: ['Searching videos for drills.', 'The same punching drill again.', 'Little ones lost, older kids bored.'],
  },
  after: {
    label: 'AFTER',
    when: 'On the mat',
    items: ['Open this week’s page.', 'Prepare in 5 minutes.', 'Teach the 45-minute class block.', 'Bow out and send the parent note.'],
  },
};

// ── 3. WHAT DOES YOUR CLASS NEED THIS WEEK (pick a need, see the class that is ready for it) ──
const block = 'Class block: bow-in & warm-up game 8 · technique 12 · pads & partners 12 · final game 8 · value talk 5';
export const selector = {
  eyebrow: 'START WHERE YOUR STUDENTS ARE',
  title: 'WHAT DOES YOUR CLASS NEED THIS WEEK?',
  sub: 'Pick a need and see the class that is ready for it.',
  startLabel: 'YOUR NEXT CLASS',
  tablistLabel: 'Class needs',
  columns: ['PAGE 1 · CLASS PLAN', 'PAGE 2 · TEACH IT', 'PAGE 3 · DRILLS & GAME'],
  note: 'All 10 classes follow this same 3-page structure. Adapt each one to your style and your school’s safety rules.',
  items: [
    {
      tab: 'A strong start: stance & respect',
      n: 'CLASS 01 · WEEK 1',
      title: 'Stance, Guard & Dojo Respect',
      goal: 'The child bows in, stands in a strong fighting stance with the guard up and returns to ready stance on command.',
      open: ['Class goal, equipment and 5-minute prep', 'Your opening words: “Feet apart, one foot back. Hands up by your face. That’s your guard!”', block],
      activity: ['5 technique steps with key points: attention, bow, ready stance, fighting stance, guard', '3 Fix It cues, like “Train tracks, not a tightrope!”', 'Age level card: how to teach it to ages 4–6, 7–9 and 10–12'],
      close: ['2 drills with mat diagrams: Stance Check Line and Bounce & Freeze', 'Final game: Dojo Says', 'Value talk (Respect), parent note and stripe check'],
    },
    {
      tab: 'Kicks with balance',
      n: 'CLASS 03 · WEEK 9',
      title: 'Front Kick & Balance',
      goal: 'The child lifts the knee, kicks forward with the ball of the foot and puts the foot down in stance with control.',
      open: ['Class goal, equipment and 5-minute prep', 'Your opening words: “Knee up… kick… knee back… foot down. Guard stays up!”', block],
      activity: ['Technique steps with key points, from knee-up to foot down', '3 Fix It cues for the most common kicking errors', 'Age level card for ages 4–6, 7–9 and 10–12'],
      close: ['2 drills on the kick shield, with mat diagrams and timing', 'Final game: Stoplight Kicks', 'Value talk (Self-Control), parent note and stripe check'],
    },
    {
      tab: 'Combinations on the pads',
      n: 'CLASS 06 · WEEK 22',
      title: 'Punch Combinations on Pads',
      goal: 'The child throws a 3-punch combination (jab, cross, hook) on a pad and returns to guard.',
      open: ['Class goal, equipment and 5-minute prep', 'Your opening words: “1 is jab, 2 is cross, 3 is hook. Ready? 1-2-3… guard!”', block],
      activity: ['Technique steps for the hook and the 1-2-3', 'How older kids hold the mitts safely for a partner', 'Age level card for ages 4–6, 7–9 and 10–12'],
      close: ['2 pad drills with mat diagrams, rounds and timing', 'A final game that builds the combination', 'Value talk (Teamwork), parent note and stripe check'],
    },
    {
      tab: 'Stranger safety & escapes',
      n: 'CLASS 09 · WEEK 35',
      title: 'Grab Escapes & Stranger Safety',
      goal: 'The child escapes a gentle wrist grab, steps back, uses a strong voice and knows to run to and tell a trusted adult.',
      open: ['Class goal, equipment and 5-minute prep', 'Your opening words: escape, step back, strong voice, run to a safe adult', block],
      activity: ['Simple escape steps, practiced slowly and gently', 'The safety habits: stay with your group, use your voice, tell a trusted adult', 'Age level card for ages 4–6, 7–9 and 10–12'],
      close: ['2 calm role-play drills with mat diagrams', 'Final game: Strong Voice Relay', 'Value talk (Safety), parent note and stripe check'],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE NEXT TO THE MAT.',
  sub: 'Open any page and read it at full size: your 52-week plan, a complete class in 3 pages, the belt test rehearsal and your class planner.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full book.',
  captions: [
    'Your 52-week plan (Q1)',
    'Class 02 · class plan',
    'Class 02 · teach it',
    'Class 02 · drills & game',
    'Class 10 · belt test stations',
    'Class planner + stripe tracker',
  ],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY CLASS',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST BOW IN AND TEACH.',
  text: 'Every week of the year is mapped: the start of one of the 10 complete classes, a ready 10-minute game or drill, or a stripe check. Each class gives you the plan, the technique steps and the drills & game in 3 pages.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 52-week plan.' },
    { title: 'PREPARE', text: 'Read the page and set up the mat in 5 minutes.' },
    { title: 'TEACH', text: 'Warm-up game, one technique, pads and partners.' },
    { title: 'BOW OUT', text: 'Final game, value talk and the parent note.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE CLASS',
  features: [
    { icon: 'goal', title: 'One clear class goal', text: 'One sentence of what the kids will do, the week to start it and what they need first.' },
    { icon: 'timer', title: 'A 45-minute class block', text: 'Bow-in & warm-up game 8 · Technique 12 · Pads & partners 12 · Final game 8 · Value talk 5.' },
    { icon: 'steps', title: 'Technique steps + Fix It cues', text: 'Numbered steps with key points, and the exact cue to fix the most common errors.' },
    { icon: 'questions', title: 'Ages 4–6, 7–9 and 10–12', text: 'How to teach the same class to every age on your mat, even in a mixed group.' },
    { icon: 'quote', title: 'Your words, word for word', text: 'Opening words, commands and the value talk, plus a pro tip and a common mistake.' },
    { icon: 'action', title: 'Drills, game + parent note', text: '2 drills and a final game with mat diagrams, a stripe check and a note for parents.' },
  ],
} as const;

// ── 5b. VALUES + AGE LEVELS (new section in this landing) ──────────────────
export const valuesBand = {
  eyebrow: 'MORE THAN KICKS AND PUNCHES',
  title: 'ONE VALUE FOR EVERY CLASS. ALL YEAR LONG.',
  sub: 'Every class closes with a short value talk, word for word, so parents see what their kids bring home.',
  values: [
    { n: '01', value: 'Respect' },
    { n: '02', value: 'Focus' },
    { n: '03', value: 'Self-Control' },
    { n: '04', value: 'Perseverance' },
    { n: '05', value: 'Confidence' },
    { n: '06', value: 'Teamwork' },
    { n: '07', value: 'Awareness' },
    { n: '08', value: 'Courage' },
    { n: '09', value: 'Safety' },
    { n: '10', value: 'Discipline' },
  ],
  agesTitle: 'EVERY CLASS ADAPTS TO THE KIDS IN FRONT OF YOU',
  ages: [
    { age: 'AGES 4–6', text: 'Floor spots, animal names, short steps said out loud and lots of games.' },
    { age: 'AGES 7–9', text: 'The full technique on the instructor’s pads, switching sides on the call.' },
    { age: 'AGES 10–12', text: 'More speed, moving with the guard up, holding pads for a partner and helping lead.' },
  ],
};

// ── 6. THE 52-WEEK PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 52-WEEK CLASS PLAN',
  title: 'THE WHOLE YEAR, MAPPED WEEK BY WEEK.',
  sub: '★ weeks start a new class. ◆ weeks are stripe checks. The weeks in between have a ready 10-minute game or drill.',
  quarters: [
    {
      q: 'Q1',
      weeks: 'WEEKS 1–13',
      name: 'BUILD THE BASE',
      full: [
        { week: '1', label: 'START CLASS 01', title: 'Stance, Guard & Dojo Respect' },
        { week: '5', label: 'START CLASS 02', title: 'Straight Punches: Jab & Cross' },
        { week: '9', label: 'START CLASS 03', title: 'Front Kick & Balance' },
        { week: '13', label: 'STRIPE CHECK', title: 'Check progress with the planner', review: true },
      ],
      activities: '+ ready 10-minute games and drills, like Statue Guard, Line-Up Race and Flamingo Freeze.',
    },
    {
      q: 'Q2',
      weeks: 'WEEKS 14–26',
      name: 'STRIKE & DEFEND',
      full: [
        { week: '14', label: 'START CLASS 04', title: 'Roundhouse Kick' },
        { week: '18', label: 'START CLASS 05', title: 'Blocks: High, Middle & Low' },
        { week: '22', label: 'START CLASS 06', title: 'Punch Combinations on Pads' },
        { week: '26', label: 'STRIPE CHECK', title: 'Check progress with the planner', review: true },
      ],
      activities: '+ ready 10-minute games and drills, like Pivot Clock, Rain Block and Combo Call-Out.',
    },
    {
      q: 'Q3',
      weeks: 'WEEKS 27–39',
      name: 'MOVE & STAY SAFE',
      full: [
        { week: '27', label: 'START CLASS 07', title: 'Footwork & Distance' },
        { week: '31', label: 'START CLASS 08', title: 'Safe Falling' },
        { week: '35', label: 'START CLASS 09', title: 'Grab Escapes & Stranger Safety' },
        { week: '39', label: 'STRIPE CHECK', title: 'Check progress with the planner', review: true },
      ],
      activities: '+ ready 10-minute games and drills, like Too Close Too Far, Rocking Chair and Safe Zone.',
    },
    {
      q: 'Q4',
      weeks: 'WEEKS 40–52',
      name: 'PUT IT ALL TOGETHER',
      full: [
        { week: '40', label: 'START CLASS 10', title: 'Belt Test Rehearsal' },
        { week: '52', label: 'STRIPE CHECK', title: 'Year-end stripe check', review: true },
      ],
      activities: '+ ready 10-minute games and drills, like Block Master, Spotlight and Dojo Relay.',
    },
  ],
  toolsTitle: 'PLUS YOUR INSTRUCTOR TOOLS',
  tools: ['When the class gets hard: 8 situations, what to say and do', 'Class planner + stripe tracker: plan any class in 5 minutes', 'Class field card to keep next to the mat'],
};

// ── 7. THE 10 COMPLETE CLASSES ─────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE CLASSES',
  title: 'TEN KIDS CLASSES. READY TO TEACH.',
  sub: 'Follow the 52-week plan, or start with the class your students need now. Each one is 3 pages: class plan, teach it, drills & game.',
  weekLabel: 'START WEEK',
  items: [
    { n: '01', week: '1', title: 'Stance, Guard & Dojo Respect', goal: 'Bow in, a strong fighting stance with the guard up and ready stance on command.' },
    { n: '02', week: '5', title: 'Straight Punches: Jab & Cross', goal: 'A jab and a cross from guard, with the hand back to the face every time.' },
    { n: '03', week: '9', title: 'Front Kick & Balance', goal: 'Knee up, kick with the ball of the foot and land in stance with control.' },
    { n: '04', week: '14', title: 'Roundhouse Kick', goal: 'Turn the base foot, knee to the side and kick a low or middle target with control.' },
    { n: '05', week: '18', title: 'Blocks: High, Middle & Low', goal: 'Three blocks on command, with the eyes on the instructor.' },
    { n: '06', week: '22', title: 'Punch Combinations on Pads', goal: 'A 3-punch combination (jab, cross, hook) on a pad and back to guard.' },
    { n: '07', week: '27', title: 'Footwork & Distance', goal: 'Move forward, back and to the side without crossing the feet, and find the right distance.' },
    { n: '08', week: '31', title: 'Safe Falling', goal: 'Fall back and to the side from a low position, chin tucked, and get up safely.' },
    { n: '09', week: '35', title: 'Grab Escapes & Stranger Safety', goal: 'Escape a gentle wrist grab, use a strong voice and run to a trusted adult.' },
    { n: '10', week: '40', title: 'Belt Test Rehearsal', goal: 'A calm practice belt test with the year’s techniques, a score sheet and a ceremony script.' },
  ],
  cta: 'GET ALL 10 CLASSES + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 BOOK',
  title: 'EVERYTHING ELSE YOUR KIDS CLASSES NEED.',
  sub: 'Games, belt tests, parent messages, pad drills and a trial class plan, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: '60 Martial Arts Games for Kids',
      hook: 'Never run out of games again.',
      title: '60 Martial Arts Games for Kids — No Equipment Needed',
      text: '60 fun, safe games to open, energize and close any class, organized by goal: warm-up, reaction, balance, focus, kicking and striking, teamwork and cool-down. Each with setup, rules, time and a safety tip.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: 'Belt Testing Kit',
      hook: 'Run calm, organized belt tests.',
      title: 'Belt Testing Kit — Requirements, Test Day Plan & Printable Certificates',
      text: 'A sample requirements checklist for every belt (adapt it to your style), a scoring sheet, a test day run sheet with timing, what to say to open and close the test, a stripe tracker and printable certificates.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'Parent Communication Pack',
      hook: 'Know exactly what to send to families.',
      title: 'Parent Communication Pack — Welcome Letter, Dojo Rules & Ready-to-Send Messages',
      text: 'A welcome letter for new students, one-page dojo rules for kids, a what-to-bring checklist, belt test announcements, a monthly values note, messages for missed classes and calm scripts for parent talks.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: '50 Pad & Partner Drills',
      hook: 'Fresh pad work for every class.',
      title: '50 Pad & Partner Drills — Ready for Any Kids Class',
      text: '50 drills with focus mitts, kick shields and partners for punches, kicks, blocks, combinations, footwork and reaction. Each with setup, rounds and timing, how to hold the pad and a safety note.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: 'The Trial Class Playbook',
      hook: 'Make every first class count.',
      title: 'The Trial Class Playbook — Run a First Class Kids and Parents Remember',
      text: 'How to welcome the family at the door, a 30-minute trial class script with games and a first technique every child can do, what to say at the end and ready-to-send follow-up messages.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT CLASS STARTS HERE',
  title: 'YOUR WHOLE YEAR OF KIDS CLASSES. ALL IN ONE BOOK.',
  kicker: 'THE COMPLETE DIGITAL BOOK',
  name: 'Martial Arts Kids Class Ready™',
  description: 'A 52-week class plan and 10 complete, ready-to-teach 45-minute kids classes, plus five bonuses: games, a belt testing kit, parent messages, pad drills and a trial class playbook.',
  mockupAlt: 'Everything included in Martial Arts Kids Class Ready™',
  includedTitle: 'YOUR BOOK INCLUDES',
  included: [
    '52-week class plan: the whole year mapped',
    '10 complete kids classes, 3 pages each',
    'A timed 45-minute class block in every class',
    'Technique steps, key points and Fix It cues',
    'Ages 4–6, 7–9 and 10–12 in every class',
    'Drills, final game, value talk and parent note',
    'Instructor tools: hard moments, planner, field card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Martial Arts Kids Class Ready™ (52-week plan + 10 complete classes)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · About 52¢ a week',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout (new copy for this landing) */
  cta: 'YES, SEND ME THE BOOK — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. Adapt every class to your style, your school’s safety rules and the age and level of your students.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Teach your next classes with it. If it isn't right for you, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOU BOW IN.',
  sub: 'Built for instructors and school owners who teach kids. A ready-to-teach class book, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF book: a 52-week class plan (every week has the start of a class, a ready 10-minute game or drill, or a stripe check) and 10 complete 45-minute kids classes of 3 pages each, plus instructor tools and 5 bonuses.',
    },
    {
      q: 'Which styles does it work for?',
      a: 'Striking styles taught to kids: karate, taekwondo, kickboxing, Muay Thai and mixed-style programs. Techniques use general names (front kick, jab, high block), and every class reminds you to use your style’s names, etiquette and belt order.',
    },
    {
      q: 'What ages is it for?',
      a: 'Kids from about 4 to 12. Every class has an age level card with how to teach it to ages 4–6, 7–9 and 10–12, so it works for mixed-age groups too.',
    },
    {
      q: 'Is there sparring or contact?',
      a: 'No sparring. Kids strike pads, targets and the air. Partner drills use light, controlled contact on pads only, always supervised, and falls are taught on mats from low positions.',
    },
    {
      q: 'Does the safety class teach self-defense?',
      a: 'Class 09 teaches simple, gentle grab escapes and safety habits: stay with your group, use a strong voice and tell a trusted adult. It is not a promise that a child can defend themselves in any situation.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Can I use the classes individually?',
      a: 'Yes. Each class stands alone, so you can start with the one your students need now, or follow the 52-week plan from week 1.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The pages are easy to read on a phone or tablet next to the mat, and the class planner and stripe tracker are ready to print.',
    },
    {
      q: 'Can I share it with my instructors?',
      a: 'This purchase is for your own use as an instructor. Sharing it across several schools, redistributing it or reselling it is not included in this offer.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT DO I TEACH THE KIDS TONIGHT?”',
  title: 'WALK ONTO THE MAT WITH TONIGHT’S CLASS READY.',
  text: 'Open this week’s page. Prepare in 5 minutes. Teach a class the kids want to come back to.',
  microcopy: '52-week plan + 10 complete classes + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'MARTIAL ARTS KIDS CLASS READY™ · Class material for instructors. Always follow your style’s rules and your school’s safety policies.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
